// Offline job-capture controller (task 3.7 — Req 3.6).
//
// The browser-side wiring for the offline queue: it opens the IndexedDB-backed
// store, exposes high-level operations the jobs screen uses (create-or-queue, list
// pending, flush), and drains the queue automatically when connectivity returns
// (the `online` event) and once on app load (to clear any backlog from a previous
// session).
//
// The pure queue policy (what to keep, drop or retry) lives in
// ./lib/offlineQueue.js and is unit-tested there; this file is the thin,
// browser-only glue that talks to the real API through ./api.js — so a queued
// capture is POSTed exactly as a live one would be (Req 1.3 auth still applies).
//
// UI updates: whenever the queue changes (an item is queued, sent or dropped) we
// dispatch a `sparky:offline-queue-changed` window event. Screens showing pending
// state (the jobs list) listen for it and re-render.

import { api } from './api.js';
import {
  DB_NAME,
  DB_VERSION,
  STORE_NAME,
  makeQueuedJob,
  createMemoryStore,
  flushQueue,
  classifyError,
} from './lib/offlineQueue.js';

// Dispatched (on `window`) whenever the pending queue changes size.
export const QUEUE_CHANGED_EVENT = 'sparky:offline-queue-changed';

// --- IndexedDB store -------------------------------------------------------

function hasIndexedDb() {
  return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Wrap an IDBRequest as a promise.
function reqAsPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Build the store contract (add/getAll/delete/count/clear) over an open DB. Falls
// back to a non-persistent in-memory store where IndexedDB is unavailable (e.g. a
// hostile private-mode browser) so the app degrades rather than breaking.
async function openJobQueueStore() {
  if (!hasIndexedDb()) return createMemoryStore();
  let db;
  try {
    db = await openDb();
  } catch {
    return createMemoryStore();
  }
  const objectStore = (mode) => db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
  return {
    add: (item) => reqAsPromise(objectStore('readwrite').add(item)).then(() => item),
    getAll: () => reqAsPromise(objectStore('readonly').getAll()),
    delete: (id) => reqAsPromise(objectStore('readwrite').delete(id)),
    count: () => reqAsPromise(objectStore('readonly').count()),
    clear: () => reqAsPromise(objectStore('readwrite').clear()),
  };
}

// The store is opened once and reused for the app's lifetime.
let _storePromise = null;
function store() {
  if (!_storePromise) _storePromise = openJobQueueStore();
  return _storePromise;
}

function emitChanged() {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(QUEUE_CHANGED_EVENT));
  }
}

// True unless the browser reports it is definitely offline. (When `navigator` or
// `onLine` is unavailable we optimistically assume online and let a real failure
// route us into the queue.)
export function isOnline() {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

// POST a queued (or live) job capture to the API. The same call path a normal
// create uses, so the ID token is attached (Req 1.3).
const sendJob = (item) => api.post(`/clients/${encodeURIComponent(item.clientId)}/jobs`, item.payload);

// --- Public operations -----------------------------------------------------

// Create a job now, or queue it if we're offline / the POST fails on connectivity.
// Returns { status: 'created', job } or { status: 'queued', item }. Real
// (non-connectivity) API errors are re-thrown so the form can show them.
export async function createJobOrQueue({ clientId, payload }) {
  if (!isOnline()) return queue(clientId, payload);
  try {
    const job = await api.post(`/clients/${encodeURIComponent(clientId)}/jobs`, payload);
    return { status: 'created', job };
  } catch (err) {
    if (classifyError(err) === 'offline') return queue(clientId, payload);
    throw err;
  }
}

async function queue(clientId, payload) {
  const item = makeQueuedJob({ clientId, payload });
  await (await store()).add(item);
  emitChanged();
  return { status: 'queued', item };
}

// All pending captures, oldest first (for the pending UI).
export async function listQueuedJobs() {
  const items = await (await store()).getAll();
  return items.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
}

export async function countQueuedJobs() {
  return (await store()).count();
}

// Drain the queue through the API. Guarded so overlapping triggers (an `online`
// event landing while a flush is mid-flight) don't double-send. No-op while
// offline. Emits a change event if anything left the queue.
let _flushing = false;
export async function flushQueuedJobs() {
  if (_flushing || !isOnline()) return null;
  _flushing = true;
  try {
    const result = await flushQueue(await store(), sendJob);
    if (result.sent || result.dropped) emitChanged();
    return result;
  } catch {
    // A store/transaction failure shouldn't crash the app; try again next trigger.
    return null;
  } finally {
    _flushing = false;
  }
}

// --- Lifecycle -------------------------------------------------------------

// Wire automatic flushing: on every reconnect, and once now to clear any backlog
// carried over from a previous session. Idempotent — safe to call on each
// authenticated app render.
let _started = false;
export function initOfflineSync() {
  if (_started || typeof window === 'undefined') return;
  _started = true;
  window.addEventListener('online', () => { flushQueuedJobs(); });
  flushQueuedJobs();
}

// Test/teardown seam: reset module state (does not remove the `online` listener).
export function _resetForTests() {
  _storePromise = null;
  _flushing = false;
  _started = false;
}
